import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Toolbar,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  LinearProgress
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as NumberListIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  Code as CodeIcon,
  FormatQuote as QuoteIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  FormatSize as HeaderIcon
} from '@mui/icons-material';
import { ImageUploadService } from '../services/imageUploadService';

interface WYSIWYGEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  maxHeight?: number;
  disabled?: boolean;
}

const WYSIWYGEditor: React.FC<WYSIWYGEditorProps> = ({
  value,
  onChange,
  placeholder = 'اكتب المحتوى هنا...',
  minHeight = 300,
  maxHeight = 600,
  disabled = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // تطبيق تنسيق النص
  const execCommand = useCallback((command: string, value?: string) => {
    if (disabled) return;
    
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [disabled, onChange]);

  // معالجة تغيير المحتوى
  const handleContentChange = useCallback(() => {
    if (editorRef.current && !disabled) {
      onChange(editorRef.current.innerHTML);
    }
  }, [disabled, onChange]);

  // معالجة رفع الصور
  const handleImageUpload = async (file: File) => {
    let progressInterval: NodeJS.Timeout | null = null;

    try {
      setImageUploading(true);
      setUploadProgress(0);
      setError(null);

      // محاكاة تقدم الرفع
      progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // استخدام منطق رفع صور المنتجات الذي يعمل بشكل صحيح
      const result = await ImageUploadService.uploadForMaintenanceBank(file);

      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      setUploadProgress(100);

      // إدراج الصورة في المحرر
      const imageHtml = `<img src="${result.url}" alt="${result.fileName || 'صورة'}" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
      execCommand('insertHTML', imageHtml);

      setImageDialogOpen(false);
      setUploadProgress(0);
    } catch (error: any) {
      console.error('❌ خطأ في رفع الصورة:', error);

      // رسائل خطأ مفصلة حسب نوع المشكلة
      let errorMessage = 'حدث خطأ أثناء رفع الصورة';

      if (error.message.includes('مفاتيح ImageKit')) {
        errorMessage = '⚙️ يرجى إعداد مفاتيح ImageKit في الإعدادات المتقدمة';
      } else if (error.message.includes('ImgBB API')) {
        errorMessage = '🔑 يرجى إعداد مفتاح ImgBB API في الإعدادات المتقدمة';
      } else if (error.message.includes('401')) {
        errorMessage = '🔐 خطأ في المصادقة - يرجى التحقق من المفاتيح';
      } else if (error.message.includes('413') || error.message.includes('كبير')) {
        errorMessage = '📏 حجم الصورة كبير جداً (الحد الأقصى 5 ميجابايت)';
      } else if (error.message.includes('400')) {
        errorMessage = '📄 نوع الملف غير مدعوم أو تالف';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = '🌐 مشكلة في الاتصال بالإنترنت';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setUploadProgress(0);
    } finally {
      setImageUploading(false);
    }
  };

  // معالجة إدراج الرابط
  const handleInsertLink = () => {
    if (linkUrl && linkText) {
      const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      execCommand('insertHTML', linkHtml);
      setLinkDialogOpen(false);
      setLinkUrl('');
      setLinkText('');
    }
  };

  // أزرار شريط الأدوات
  const toolbarButtons: Array<{ icon?: React.ReactNode; command?: string; value?: string; tooltip?: string; divider?: boolean }> = [
    { icon: <UndoIcon />, command: 'undo', tooltip: 'تراجع' },
    { icon: <RedoIcon />, command: 'redo', tooltip: 'إعادة' },
    { divider: true },
    { icon: <BoldIcon />, command: 'bold', tooltip: 'عريض' },
    { icon: <ItalicIcon />, command: 'italic', tooltip: 'مائل' },
    { icon: <UnderlineIcon />, command: 'underline', tooltip: 'تحته خط' },
    { divider: true },
    { icon: <AlignLeftIcon />, command: 'justifyLeft', tooltip: 'محاذاة يسار' },
    { icon: <AlignCenterIcon />, command: 'justifyCenter', tooltip: 'محاذاة وسط' },
    { icon: <AlignRightIcon />, command: 'justifyRight', tooltip: 'محاذاة يمين' },
    { divider: true },
    { icon: <BulletListIcon />, command: 'insertUnorderedList', tooltip: 'قائمة نقطية' },
    { icon: <NumberListIcon />, command: 'insertOrderedList', tooltip: 'قائمة مرقمة' },
    { divider: true },
    { icon: <QuoteIcon />, command: 'formatBlock', value: 'blockquote', tooltip: 'اقتباس' },
    { icon: <CodeIcon />, command: 'formatBlock', value: 'pre', tooltip: 'كود' },
  ];

  return (
    <Paper elevation={2} sx={{ border: '1px solid #e0e0e0' }}>
      {/* شريط الأدوات */}
      <Toolbar 
        variant="dense" 
        sx={{ 
          minHeight: 48, 
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          flexWrap: 'wrap',
          gap: 0.5
        }}
      >
        {toolbarButtons.map((button, index) => (
          button.divider ? (
            <Divider key={index} orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          ) : (
            <Tooltip key={index} title={button.tooltip || ''}>
              <IconButton
                size="small"
                onClick={() => button.command && execCommand(button.command, button.value)}
                disabled={disabled}
                sx={{
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  borderRadius: 1
                }}
              >
                {button.icon}
              </IconButton>
            </Tooltip>
          )
        ))}
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        
        {/* أزرار خاصة */}
        <Tooltip title="إدراج صورة">
          <IconButton
            size="small"
            onClick={() => setImageDialogOpen(true)}
            disabled={disabled}
            sx={{ borderRadius: 1 }}
          >
            <ImageIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="إدراج رابط">
          <IconButton
            size="small"
            onClick={() => setLinkDialogOpen(true)}
            disabled={disabled}
            sx={{ borderRadius: 1 }}
          >
            <LinkIcon />
          </IconButton>
        </Tooltip>

        {/* اختيار حجم العنوان */}
        <Tooltip title="عنوان">
          <IconButton
            size="small"
            onClick={() => execCommand('formatBlock', 'h3')}
            disabled={disabled}
            sx={{ borderRadius: 1 }}
          >
            <HeaderIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {/* منطقة المحرر */}
      <Box
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleContentChange}
        dangerouslySetInnerHTML={{ __html: value }}
        sx={{
          minHeight,
          maxHeight,
          overflow: 'auto',
          p: 2,
          outline: 'none',
          '&:focus': {
            bgcolor: '#fafafa'
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 1,
            boxShadow: 1
          },
          '& blockquote': {
            borderLeft: '4px solid #2196f3',
            pl: 2,
            ml: 0,
            fontStyle: 'italic',
            color: 'text.secondary'
          },
          '& pre': {
            bgcolor: '#f5f5f5',
            p: 1,
            borderRadius: 1,
            overflow: 'auto',
            fontFamily: 'monospace'
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }
        }}
        dir="rtl"
      />

      {/* حوار رفع الصور */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>إدراج صورة</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {imageUploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                جاري رفع الصورة...
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
          
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleImageUpload(file);
              }
            }}
            style={{ width: '100%', padding: '10px' }}
            disabled={imageUploading}
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            الحد الأقصى: 5 ميجابايت. الأنواع المدعومة: JPG, PNG, GIF, WebP
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)} disabled={imageUploading}>
            إلغاء
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار إدراج الرابط */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>إدراج رابط</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="عنوان الرابط (URL)"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            placeholder="https://example.com"
          />
          <TextField
            fullWidth
            label="نص الرابط"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            placeholder="اضغط هنا"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>
            إلغاء
          </Button>
          <Button 
            onClick={handleInsertLink} 
            variant="contained"
            disabled={!linkUrl || !linkText}
          >
            إدراج
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default WYSIWYGEditor;
